import {
    Button,
    Caption,
    Flex,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './Contacts.mobile.style';
import { useTracker } from '@archipro-website/tracker';
import EnquiryModal from '~/modules/enquiry/component/enquiry-modal/EnquiryModal';
import { Link, useLoaderData } from '@remix-run/react';
import type { ProjectPageLoader } from '@modules/project/type';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { trackProjectEnquire } from '~/modules/tracking/util/trackProjectEnquiry';
import { getPrimaryCTA } from '~/modules/professional/util/professional-cta-helper';
import BookProfessional from '@modules/professional/component/header-section/BookProfessional';
import { useAppData } from '@modules/root';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import { useSaveToDesignBoard } from '~/modules/design-board/hook';
import { PrimaryCtaEnum } from '../../../../../generated/graphql';

interface ContactsData {
    ID: number;
    Website?: string | null;
    Professional: ProjectPageLoader['project']['Professional'];
    categoryPath?: string;
}

export interface Props {
    data: ContactsData;
}

const Contacts = ({ data: { ID, Professional, categoryPath = '' } }: Props) => {
    const { css } = useStyles();
    const tracker = useTracker();
    const { project } = useLoaderData<typeof loader>();
    const { bookConsultationEnabled } = useAppData();
    const { favouriteItem } = useSaveToDesignBoard({
        itemType: 'ProjectPin',
        itemId: project.ID,
    });
    const showBookConsultation =
        Professional.TeamCalendarURLSegment &&
        bookConsultationEnabled &&
        Professional.PrimaryCTA === PrimaryCtaEnum.BookConsultation;

    const enquiryProfessional = {
        ...Professional,
        Website: Professional.Website ?? '',
        TeamCalendarURLSegment:
            Professional.TeamCalendarURLSegment ?? undefined,
        CompanyLogo: Professional.CompanyLogo ?? '',
        LogoBackgroundColor: Professional.LogoBackgroundColor ?? 'white',
        primaryCTA: getPrimaryCTA({
            primaryCTA: Professional.PrimaryCTA,
        }),
        AvailableForWork: Professional.AvailableForWork,
    };

    return (
        <Flex column className={css(S.Contacts)}>
            <div className={css(S.EnquireContainer)}>
                {showBookConsultation ? (
                    <BookProfessional
                        professional={enquiryProfessional}
                        enquiryDrawer={true}
                        enquiryType={'project'}
                        trackingRecordID={ID}
                        extraTracking={() =>
                            trackProjectEnquire(tracker, {
                                category: categoryPath,
                                step: 1,
                                project: project.Title,
                                professional: Professional.Title,
                            })
                        }
                    />
                ) : (
                    <Flex>
                        <EnquiryModal
                            professional={{
                                ...Professional,
                                Website: Professional.Website ?? '',
                                CompanyLogo: Professional.CompanyLogo ?? '',
                                LogoBackgroundColor:
                                    Professional.LogoBackgroundColor ?? 'white',
                                primaryCTA: getPrimaryCTA({
                                    primaryCTA: Professional.PrimaryCTA,
                                }),
                            }}
                            relatedItem={{
                                id: project.ID,
                                type: 'project',
                                title: project.Title,
                                category: categoryPath,
                            }}
                            branchOptions={Professional.branchOptions}
                            trigger={
                                <Button
                                    size={16}
                                    color="dark"
                                    className={css(S.EnquireButton)}
                                    onClick={() => {
                                        trackEnquireReveal(tracker, {
                                            professionalName:
                                                project.Professional.Title,
                                            itemID: project.ID,
                                            itemType: 'project',
                                            source: 'EnquiryModal',
                                        });

                                        trackProjectEnquire(tracker, {
                                            category: categoryPath,
                                            step: 1,
                                            project: project.Title,
                                            professional: Professional.Title,
                                        });
                                    }}
                                >
                                    ENQUIRE NOW
                                </Button>
                            }
                            drawer={true}
                            siteTreeID={project.ID}
                            submitButtonText={'Send Enquiry'}
                            messageBodyPlaceholder={
                                'Enquire about this Project'
                            }
                            onViewProfile={() => {
                                tracker.log('ProjectProfileButtonClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: [
                                        'archiproTracker',
                                        'ga4Tracker',
                                    ],
                                });
                            }}
                        />
                        <SaveToDesignBoard
                            heartButton={{
                                className: css(S.ButtonHeart),
                                color: 'primary',
                                variables: {
                                    iconPadding: 0,
                                    buttonPadding: 0,
                                    primaryNoBKGButtonBackgroundColorHover:
                                        'transparent',
                                },
                                size: 20,
                            }}
                            {...favouriteItem}
                        />
                    </Flex>
                )}
            </div>
            <Flex
                gap="space-20"
                className={css(S.SaveShareContainer)}
                hAlign="center"
                vAlign="center"
            >
                <Button
                    className={css(S.ProfileLink)}
                    as={Link}
                    to={Professional.Link}
                    size={16}
                    color="dark"
                    transparent
                    outlined
                    variables={{
                        buttonSize20LineHeight: '130%',
                        buttonPadding: pxToRem(15),
                        buttonMinWidth: 'fit-content',
                    }}
                    onClick={() => {
                        tracker.log('ProjectProfileButtonClick', {
                            url: new URL(window.location.href),
                            targetTracker: ['archiproTracker', 'ga4Tracker'],
                        });
                    }}
                >
                    VIEW PROFILE
                </Button>
            </Flex>
            {Professional.EnquiryResponseRate &&
            Professional.EnquiryResponseRate >= 40 ? (
                <div className={css(S.ResposeRate)}>
                    <Caption
                        variant="02"
                        variables={{
                            letterSpacing: 'normal',
                            fontSize: pxToRem(14),
                        }}
                    >
                        {`Responds within 24 hours: ${Professional.EnquiryResponseRate}%`}
                    </Caption>
                </div>
            ) : null}
        </Flex>
    );
};

export default Contacts;
